import type { InvoiceLineItem } from '@/types/invoice';

/**
 * NOT-628 — member pricing inside the invoice editor.
 *
 * An invoice converted from an estimate carries the member's price on its
 * lines. The editor used to total those lines at LIST: it showed a $1,300.50
 * invoice as $1,531.00, warned that the invoice "exceeds job total by $230.50"
 * as if the operator had added scope, and saved that number back — billing the
 * member the non-member price.
 */

function membershipSavingOf(item: Pick<InvoiceLineItem, 'membershipDiscountAmount'>): number {
  return Math.max(0, item.membershipDiscountAmount ?? 0);
}

/**
 * The line's share of a document-level discount.
 *
 * Allocated per line by the pricing engine (`applyDocumentDiscount`) BEFORE tax
 * is resolved, so it comes off the taxable base exactly as the membership
 * saving does. An editor that ignored it totalled a discounted job at its
 * undiscounted price.
 */
function documentSavingOf(item: Pick<InvoiceLineItem, 'documentDiscountAmount'>): number {
  return Math.max(0, item.documentDiscountAmount ?? 0);
}

/** What a line is worth before its membership saving comes off. */
export function lineValueBeforeMembership(
  item: Pick<InvoiceLineItem, 'quantity' | 'unitPrice' | 'discountPercent'>,
): number {
  return item.quantity * item.unitPrice * (1 - (item.discountPercent ?? 0) / 100);
}

/**
 * What a line is taxed on: its value less every saving that comes off before
 * tax. Both savings are applied ahead of `resolveLineTaxes` in the pricing
 * engine, so taxing the pre-saving value quotes a tax the invoice never bills.
 */
export function taxableBaseOf(
  item: Pick<
    InvoiceLineItem,
    | 'quantity'
    | 'unitPrice'
    | 'discountPercent'
    | 'membershipDiscountAmount'
    | 'documentDiscountAmount'
  >,
): number {
  return Math.max(
    0,
    lineValueBeforeMembership(item) - membershipSavingOf(item) - documentSavingOf(item),
  );
}

/**
 * A line's own total, from the figures it currently carries.
 *
 * Lives here, beside the savings it subtracts, because every function in this
 * file that CHANGES one of those figures has to leave the line's total saying
 * the same thing the summary does — a row reading $85 under a summary of $70
 * is the mismatch this whole area exists to stop.
 */
export function recomputeLineTotal<T extends InvoiceLineItem>(item: T): T {
  const taxableSubtotal = taxableBaseOf(item);
  return {
    ...item,
    lineTotal:
      taxableSubtotal +
      taxableSubtotal * ((item.taxable === false ? 0 : (item.taxRate ?? 0)) / 100),
  };
}

export interface InvoiceEditorTotals {
  subtotal: number;
  discountTotal: number;
  membershipDiscountTotal: number;
  documentDiscountTotal: number;
  taxTotal: number;
  total: number;
}

/**
 * The editor's running totals. Tax is charged on what the customer actually
 * pays, so the membership saving comes off the taxable base — the same order
 * the biller uses.
 */
export function computeInvoiceEditorTotals(items: InvoiceLineItem[]): InvoiceEditorTotals {
  const subtotal = items.reduce((sum, item) => sum + item.quantity * item.unitPrice, 0);
  const discountTotal = items.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice * ((item.discountPercent ?? 0) / 100),
    0,
  );
  const membershipDiscountTotal = items.reduce((sum, item) => sum + membershipSavingOf(item), 0);
  const documentDiscountTotal = items.reduce((sum, item) => sum + documentSavingOf(item), 0);
  const taxTotal = items.reduce(
    (sum, item) =>
      sum + taxableBaseOf(item) * ((item.taxable === false ? 0 : (item.taxRate ?? 0)) / 100),
    0,
  );
  return {
    subtotal,
    discountTotal,
    membershipDiscountTotal,
    documentDiscountTotal,
    taxTotal,
    total: subtotal - discountTotal - membershipDiscountTotal - documentDiscountTotal + taxTotal,
  };
}

/**
 * Carry a line's membership saving across an edit as a RATE of what the line is
 * worth — the rule the server re-derives on save.
 *
 * Held as a fixed amount, doubling the quantity kept the saving at one unit's
 * worth in this preview while the saved invoice charged two, so the operator
 * agreed to a total the customer never got.
 */
export function rescaleMembershipDiscount(
  previous: InvoiceLineItem,
  updated: InvoiceLineItem,
): InvoiceLineItem {
  const previousDiscount = membershipSavingOf(previous);
  if (previousDiscount <= 0) return updated;
  const previousValue = lineValueBeforeMembership(previous);
  const nextValue = lineValueBeforeMembership(updated);
  if (!(previousValue > 0) || !(nextValue > 0)) {
    return { ...updated, membershipDiscountAmount: 0 };
  }
  const rate = Math.min(1, previousDiscount / previousValue);
  return {
    ...updated,
    membershipDiscountAmount: Math.round(nextValue * rate * 100) / 100,
  };
}

/** A document-level discount as the document states it, before allocation. */
export interface DocumentDiscountRule {
  type: 'PERCENT' | 'FLAT';
  value: number;
}

/**
 * A JOB's document discount as the job carries it: the rule, and each job
 * line's share of it by job line key (only the lines holding one). The shares
 * are the job's own allocation, so they add up to the job's discount exactly.
 */
export interface JobDocumentDiscount {
  rule: DocumentDiscountRule;
  shares: Record<string, number>;
}

function isJobDocumentDiscount(
  discount: DocumentDiscountRule | JobDocumentDiscount,
): discount is JobDocumentDiscount {
  return 'shares' in discount;
}

/**
 * NOT-778 — the job's discount over the lines of an invoice that bills part
 * of the job, the way create() and the edit save price them.
 *
 * The discount is one amount on the job. Holding every job line that carries a
 * share, the invoice bills it whole under the job's rule, re-spread over its
 * lines as before. Holding only some, it takes exactly those lines' shares and
 * nothing else takes any — so a second invoice for the rest takes the rest,
 * instead of the whole discount again. The server prices the same way
 * (`invoiceShareOfJobDiscount` in operations), so the draft is what it saves.
 */
function pinJobDiscountShares<T extends InvoiceLineItem>(
  items: T[],
  discount: JobDocumentDiscount,
): T[] | null {
  const billed = new Set(
    items.flatMap((item) => (item.sourceJobLineKey ? [item.sourceJobLineKey] : [])),
  );
  const keys = Object.keys(discount.shares);
  if (billed.size === 0 || keys.every((key) => billed.has(key))) return null;
  // A job line on the draft twice takes its share once — the same rule the
  // server prices by.
  const taken = new Set<string>();
  return items.map((item) => {
    let shareCents = 0;
    const key = item.sourceJobLineKey;
    if (key && !taken.has(key) && !isMembershipLine(item)) {
      const base = Math.max(0, lineValueBeforeMembership(item) - membershipSavingOf(item));
      // Capped at the whole cents the line has, floored as the engine floors
      // it: a $1.005 line gives up $1.00, never $1.01. The epsilon only keeps
      // binary floating point from reading 59.97 as 5996.9999 cents.
      shareCents = Math.max(
        0,
        Math.min(Math.round((discount.shares[key] ?? 0) * 100), Math.floor(base * 100 + 1e-6)),
      );
    }
    if (key) taken.add(key);
    if (shareCents <= 0 && item.documentDiscountAmount === undefined) return item;
    return recomputeLineTotal({ ...item, documentDiscountAmount: shareCents / 100 });
  });
}

/**
 * A document discount spread over the lines this invoice actually bills.
 *
 * The rule belongs to the document, the allocation does not: a FLAT discount
 * is one amount shared out, so dropping a line or growing one changes every
 * other line's share. Carried as fixed per-line amounts, the editor quoted the
 * allocation of a DIFFERENT invoice — the one the preview priced, before the
 * operator chose what to bill. Two $100 lines sharing a flat $30 start at $15
 * each; bill only one and the whole $30 belongs to it, which is what create()
 * does with the lines it is given.
 *
 * The same order the engine uses (`applyDocumentDiscount`): membership lines
 * and lines worth nothing take no share, the rest split it in whole cents by
 * what they are worth after their own discount and membership saving, and the
 * last of them takes the rounding remainder — capped, always, at what the line
 * still has to give.
 *
 * Given the JOB's discount instead of a rule, an invoice billing only part of
 * the job takes its lines' shares of it (`pinJobDiscountShares`); one billing
 * the whole of it spreads the job's rule as above.
 */
export function allocateDocumentDiscount<T extends InvoiceLineItem>(
  items: T[],
  discount: DocumentDiscountRule | JobDocumentDiscount | null | undefined,
): T[] {
  // Nobody has said yet. A preview still in flight, or an endpoint that does
  // not return the rule, must not be read as "this document discounts
  // nothing" — clearing the shares a discounted invoice already carries makes
  // the form total jump above what the save will write.
  if (discount === undefined) return items;
  if (discount && isJobDocumentDiscount(discount)) {
    const pinned = pinJobDiscountShares(items, discount);
    if (pinned) return pinned;
  }
  const rule = discount && isJobDocumentDiscount(discount) ? discount.rule : discount;
  const bases = items.map((item) =>
    String(item.type ?? '').toUpperCase() === 'MEMBERSHIP'
      ? 0
      : Math.max(0, lineValueBeforeMembership(item) - membershipSavingOf(item)),
  );
  const eligible = bases.flatMap((base, index) => (base > 0 ? [index] : []));
  const baseCents = eligible.reduce((sum, index) => sum + Math.round(bases[index]! * 100), 0);
  const discountCents =
    !rule || !(rule.value > 0) || eligible.length === 0 || baseCents <= 0
      ? 0
      : Math.min(
          rule.type === 'PERCENT'
            ? Math.round((baseCents * rule.value) / 100)
            : Math.round(rule.value * 100),
          baseCents,
        );

  const shares = new Map<number, number>();
  let allocated = 0;
  eligible.forEach((index, position) => {
    const lineCents = Math.round(bases[index]! * 100);
    const remaining = discountCents - allocated;
    const share =
      position === eligible.length - 1
        ? Math.min(remaining, lineCents)
        : Math.min(Math.round((discountCents * lineCents) / baseCents), lineCents, remaining);
    shares.set(index, share);
    allocated += share;
  });

  return items.map((item, index) => {
    const share = (shares.get(index) ?? 0) / 100;
    // Absent stays absent: a draft with no document discount must not start
    // carrying a $0 field the server never sent.
    if (share <= 0 && item.documentDiscountAmount === undefined) return item;
    // Recomputed, not just reassigned. Re-spreading a discount changes what a
    // line is worth, and a row left at its old total reads against the summary
    // the same numbers now add up to.
    return recomputeLineTotal({ ...item, documentDiscountAmount: share });
  });
}

/** A line that sells the membership itself, rather than work priced under it. */
function isMembershipLine(item: InvoiceLineItem): boolean {
  return String(item.type ?? '').toUpperCase() === 'MEMBERSHIP';
}

/**
 * Member prices the invoice will not honor, taken back off the draft.
 *
 * A job sold with a membership carries the member price on every line AND the
 * membership's own fee line. The server honors that frozen discount only when
 * the fee is billed on THIS invoice — paying it is what activates the
 * membership — or when the customer already holds one
 * (`frozenMembershipDiscountsIfHonorable`). So an invoice that bills the work
 * and leaves the fee behind is billed at list, and a draft still showing the
 * savings quotes the customer LESS than they will be asked for.
 *
 * Conservative on purpose: when the customer turns out to already be a member
 * the invoice comes in under the draft, which is the harmless direction.
 */
export function withoutUnearnedMembership<T extends InvoiceLineItem>(
  lines: T[],
  jobSellsMembership: boolean,
): T[] {
  if (!jobSellsMembership || lines.some(isMembershipLine)) return lines;
  return lines.map((item) =>
    membershipSavingOf(item) > 0
      ? recomputeLineTotal({ ...item, membershipDiscountAmount: 0 })
      : item,
  );
}
